/* @flow */

import classNames from 'classnames';
import { ErrorMessage as FormikErrorMessage } from 'formik';
import * as React from 'react';
import createUseThemeStyles from 'src/shared/app/base/util/createUseThemeStyles';

import styles from './FormGroup.style';

import FormikDatalist from './FormikDatalist';
import FormikInput from './FormikInput';
import FormikTextArea from './FormikTextArea';
import LengthCounter from 'src/shared/app/base/component/data-entry/length-counter/LengthCounter';
import FormikSelect from 'src/shared/app/base/component/data-entry/form/FormikSelect';
import ErrorMessage from 'src/shared/ui/component/form/ErrorMessage';
import Label from 'src/shared/ui/component/form/Label';

type Props = {
  isTouchDevice: boolean,
  inputProps: Object,
};

const useStyles = createUseThemeStyles(styles);

const FormikFormGroupDumb = ({
  isTouchDevice,
  inputProps,
}: Props): React.Node => {
  const {
    id,
    componentOpts,
    field,
    form,
    maxLength,
    onFocus = () => null,
    placeholder,
  } = inputProps;
  const { name, value } = field;
  const {
    useTextarea,
    useTextareaMinified,
    isHighlighted,
    hasLengthCounter,
    hideError,
    label,
    labelHelper,
    labelClassName,
    helper,
    list,
    inputComp,
    inputOpts,
    className: rootClassName,
    ariaHidden,
  } = componentOpts || {};
  const isLengthCounterShowm = !!(hasLengthCounter && maxLength);
  const error = form.errors[name];
  const touched = form.touched[name];
  const safeProps = { ...inputProps };

  delete safeProps.componentOpts;
  delete safeProps.field;
  delete safeProps.form;
  delete safeProps.innerRef;

  safeProps.autoFocus = !isTouchDevice && safeProps.autoFocus;
  safeProps.ref = inputProps.innerRef;

  const errorId = `${id}-error`;
  if (!hideError && error) {
    safeProps['aria-describedby'] = errorId;
  }
  if (!label && placeholder) {
    safeProps['aria-label'] = placeholder;
  }
  const helpId = `${id}-help`;
  /*
   |----------------------------------------------------------------------------
   | Hooks
   |----------------------------------------------------------------------------
   */

  /*
   |----------------------------------------------------------------------------
   | State
   |----------------------------------------------------------------------------
   */

  const [isFocused, setFocused] = React.useState(false);

  /*
   |----------------------------------------------------------------------------
   | Event Handlers
   |----------------------------------------------------------------------------
   */

  const handleOnFocus = () => {
    onFocus();
    setFocused(true);
  };

  const onBlur = () => setFocused(false);

  /*
   |----------------------------------------------------------------------------
   | Classes
   |----------------------------------------------------------------------------
   */

  const classes = useStyles();

  const inputClasses = classNames(classes.input, {
    [`${classes['input-isInvalid']}`]: error && touched,
  });

  /*
   |----------------------------------------------------------------------------
   | Elements
   |----------------------------------------------------------------------------
   */
  const labelId = label ? `${id}-label` : null;
  const hideLabelForID = inputComp === FormikSelect;
  const labelElt = label && (
    <Label
      className={labelClassName}
      htmlFor={!hideLabelForID ? id : null}
      id={labelId}
    >
      {label}
      {labelHelper}
    </Label>
  );

  const errorElt = !hideError && error && (
    <FormikErrorMessage name={name}>
      {(e) => <ErrorMessage id={errorId}>{e}</ErrorMessage>}
    </FormikErrorMessage>
  );

  const helperElt = helper && !error && (
    <span className={classes.helper} id={helpId}>
      {helper}
    </span>
  );
  if (helperElt) {
    safeProps['aria-describedby'] = helpId;
  }
  let inputElt;

  if (useTextarea || useTextareaMinified) {
    inputElt = (
      <FormikTextArea
        className={inputClasses}
        field={field}
        inputProps={safeProps}
        isHighlighted={isHighlighted}
        isMinified={useTextareaMinified}
        hasError={!!error}
        hasLengthCounter={hasLengthCounter}
        handleFocus={handleOnFocus}
        handleBlur={onBlur}
      ></FormikTextArea>
    );
  } else if (list) {
    inputElt = (
      <FormikDatalist
        className={inputClasses}
        field={field}
        inputProps={safeProps}
        list={list}
        handleFocus={handleOnFocus}
        handleBlur={onBlur}
      ></FormikDatalist>
    );
  } else if (inputComp) {
    let props = {
      field,
      form,
      labelId,
      ...safeProps,
    };

    if (inputOpts) {
      props = {
        ...props,
        ...inputOpts,
      };
    }

    inputElt = React.createElement(inputComp, props);
  } else {
    inputElt = (
      <FormikInput
        className={inputClasses}
        field={field}
        inputProps={safeProps}
        handleFocus={handleOnFocus}
        handleBlur={onBlur}
      ></FormikInput>
    );
  }

  let maxLengthElt;

  if (isLengthCounterShowm) {
    const length = value ? value.length : 0;
    const isOverLimit = length > Number(maxLength);

    if (isFocused || isOverLimit) {
      maxLengthElt = (
        <span className={classes.maxLengthCtn} tabIndex="-1" role="status">
          <LengthCounter count={length} maxLength={parseInt(maxLength, 10)} />
        </span>
      );
    }
  }

  return (
    <div
      className={classNames([classes.root, rootClassName])}
      aria-hidden={ariaHidden}
    >
      {labelElt}
      {inputElt}
      {errorElt}
      {maxLengthElt}
      {helperElt}
    </div>
  );
};

export default React.memo<Props>(FormikFormGroupDumb);
